import React from "react";
import { Router, Route, browserHistory, IndexRoute } from "react-router";
import axios from "axios";
import App from "shared/App";
import FoodstarterLayout from "shared/components/FoodstarterLayout";
import GlobalContainer from "shared/components/GlobalContainer";
import TermsModal from "shared/components/TermsModal";
import WelcomeContainer from "../welcome/welcome";
import Billing from "../welcome/welcome/faq/Billing";
import Changes from "../welcome/welcome/faq/Changes";
import Marketing from "../welcome/welcome/faq/Marketing";
import Pricing from "../welcome/welcome/faq/Pricing";
import Qs from "../welcome/welcome/faq/Qs";
import StartupsDashboard from "./dashboard";
import i18nConfig from "./i18nConfig.de";
import Navigation from "./navigation";
import OrderCreationContainer from "./order-creation";
import { FormGuard } from "./order-creation/helpers/FormGuard";
import { OrderUnits } from "./order-creation/subpages/OrderUnits";
import { Overview } from "./order-creation/subpages/Overview";
import { Store } from "./order-creation/subpages/Store";
import { Success } from "./order-creation/subpages/Success";
import { Welcome } from "./order-creation/subpages/Welcome";
import { DigitalizedOrdersOverviewPage } from "./digitalized-orders/DigitalizedOrdersOverviewPage";
import OrderGroupsOverviewContainer from "./order-groups-overview";
import OrderUnitDetailsContainer from "./order-unit-details";
import OrderUnitWizardContainer from "./order-unit-details/OrderUnitWizardContainer";
import OrderUnitEditorContainer from "./order-unit-editor";
import OrderUnitsOverviewContainer from "./order-units-overview";
import configureStore from "./redux-store/configureStore";
import routes from "./routes";
import { DigitalizedOrdersDetailPage } from "./digitalized-orders/DigitalizedOrdersDetailPage";
import "../shared/analytics/plausibleTracker.js";

const requiredAuth = {
  type: "StartupAccount",
  pricingTier: ["seller", null],
};

const faqRequiredAuth = {
  type: "StartupAccount",
};

const Layout = ({ initialState, children }) => {
  const { serviceTerms } = initialState.app.account;

  return (
    <FoodstarterLayout compatMode navigation={Navigation}>
      <TermsModal
        accepted={serviceTerms.accepted}
        url={serviceTerms.url}
        onAccept={async setAccepted => {
          await axios.put("/startups/terms_acceptance", {
            accepted: true,
          });
          setAccepted(true);
        }}
      />
      <GlobalContainer requiredAuth={requiredAuth}>{children}</GlobalContainer>
    </FoodstarterLayout>
  );
};

const FAQLayout = props => {
  return (
    <FoodstarterLayout compatMode navigation={Navigation}>
      <GlobalContainer requiredAuth={faqRequiredAuth}>
        {props.children}
      </GlobalContainer>
    </FoodstarterLayout>
  );
};

export default class StartupApp extends App {
  createStore() {
    return configureStore({
      i18n: this.i18nMessages(),
      ...(this.props.initialState || {}),
    });
  }

  i18nMessages() {
    return i18nConfig.messages;
  }

  renderApp() {
    return (
      <Router history={browserHistory}>
        <Route
          path="/"
          component={props => (
            <Layout initialState={this.props.initialState} {...props} />
          )}
        >
          <Route
            path="/startups/dashboard"
            component={() => <StartupsDashboard {...this.props} />}
            exact
          />
          <Route
            path="/startups/order_groups(/)"
            component={OrderGroupsOverviewContainer}
            exact
          />
          <Route
            path="/startups/order_groups/(:scope)"
            component={OrderGroupsOverviewContainer}
            exact
          />
          <Route
            path="/startups/order_units/:id/edit"
            component={router => {
              return router.location.query.isNew ? (
                <OrderUnitEditorContainer isNew />
              ) : (
                <OrderUnitEditorContainer />
              );
            }}
          />
          <Route
            path="/startups/order_units/new"
            component={OrderUnitWizardContainer}
            exact
          />
          <Route
            path="/startups/order_units/:id"
            component={OrderUnitDetailsContainer}
            exact
          />
          <Route
            path="/startups/order_units"
            component={OrderUnitsOverviewContainer}
            exact
          />
          <Route
            path={routes.newStartupsOrderPath()}
            component={OrderCreationContainer}
          >
            <IndexRoute
              component={props => (
                <FormGuard {...props}>
                  <Welcome />
                </FormGuard>
              )}
            />
            <Route
              path="store"
              component={props => (
                <FormGuard {...props}>
                  <Store
                    navigateNextPage={() =>
                      browserHistory.push(
                        `${routes.newStartupsOrderPath()}/orderunits`,
                      )
                    }
                  />
                </FormGuard>
              )}
            />
            <Route
              path="orderunits"
              component={props => (
                <FormGuard {...props}>
                  <OrderUnits
                    navigateNextPage={() =>
                      browserHistory.push(
                        `${routes.newStartupsOrderPath()}/overview`,
                      )
                    }
                    navigatePreviousPage={() =>
                      browserHistory.push(
                        `${routes.newStartupsOrderPath()}/store`,
                      )
                    }
                  />
                </FormGuard>
              )}
            />
            <Route
              path="overview"
              component={props => (
                <FormGuard {...props}>
                  <Overview
                    onSuccess={() =>
                      browserHistory.push(
                        `${routes.newStartupsOrderPath()}/success`,
                      )
                    }
                    navigatePreviousPage={() =>
                      browserHistory.push(
                        `${routes.newStartupsOrderPath()}/orderunits`,
                      )
                    }
                  />
                </FormGuard>
              )}
            />
            <Route
              path="success"
              component={props => (
                <FormGuard {...props}>
                  <Success />
                </FormGuard>
              )}
            />
          </Route>
          <Route
            path="/startups/digitalized_orders(/)"
            component={DigitalizedOrdersOverviewPage}
          />
          <Route
            path="/startups/digitalized_orders/(:scope)"
            component={DigitalizedOrdersOverviewPage}
          />
          <Route
            path="/startups/digitalized_orders/:id"
            component={DigitalizedOrdersDetailPage}
          />
          <Route path="/startups/welcome" component={WelcomeContainer} />
        </Route>
        <Route path="/faq" component={FAQLayout}>
          <Route path="marketing" component={Marketing} />
          <Route path="qs" component={Qs} />
          <Route path="pricing" component={Pricing} />
          <Route path="billing" component={Billing} />
          <Route path="changes" component={Changes} />
        </Route>
      </Router>
    );
  }
}
